import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Container } from 'reactstrap'
import absenceApi from 'services/absenceApi'
import addIcon from 'assets/img/add-icon.svg'
import editIcon from 'assets/img/edit-icon.svg'
import { Absence, AbsenceRequest } from 'Types/Absences/Absence'
import AbsenceCreateModal from './AbsenceCreateModal'
import styles from './styles.module.scss'
import 'components/ui/fullCalendar.css'
import { SnackbarContext } from 'components/ui/snackbar/context/Snackbar'
import GenericContentPage from 'components/ui/genericContentPage/GenericContentPage'
import { TimelineGroupBase, TimelineItemBase } from 'react-calendar-timeline'
import moment, { Moment } from 'moment'
import CustomTimeLine, { AbsenceView } from './customTimeline/CustomTimeline'
import userApi from 'services/userApi'
import AbsenceSkeleton from './absenceSkeleton/AbsenceSkeleton'
import DeleteModal from 'components/ui/modals/DeleteModal'
import { ILabel } from 'Types/ILabel'
import Vacation from './Vacation'
import { getAbsenceLabel } from 'const/absenceReasons'
import VacationCreateModal from './VacationCreateModal'

const VIEW_STORAGE_KEY = 'ABSENCES_VIEW'

// Remember the last Me/Everyone choice per browser; default to the user's own absences
function readStoredView(): AbsenceView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'everyone' ? 'everyone' : 'me'
  } catch {
    return 'me'
  }
}

const AbsencesLayout: React.FC = () => {
  const [createModal, setCreateModal] = useState<boolean>(false)
  const [createVacationsModal, setCreateVacationsModal] = useState<boolean>(false)
  const [absence, setAbsence] = useState<AbsenceRequest>()
  const [absences, setAbsences] = useState<TimelineItemBase<Moment>[]>([])
  const [users, setUsers] = useState<TimelineGroupBase[]>([])
  const [formUsers, setFormUsers] = useState<ILabel[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [data, setData] = useState<boolean>(false)
  const [view, setView] = useState<AbsenceView>(readStoredView)
  const snackbarContext = useContext(SnackbarContext)

  const isAdmin = localStorage.getItem('IS_ADMIN') === 'true'
  const currentUserId = Number(localStorage.getItem('USERID'))

  const [newVacationDays, setNewVacationDays] = useState<Number>(0)
  const [oldVacationDays, setOldVacationDays] = useState<Number>(0)

  // run only on the first render
  useEffect(() => {
    getAllAbsences()
    getAllUsers()
    getUserVacationDays()
  }, [])

  function changeView(newView: AbsenceView) {
    setView(newView)
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, newView)
    } catch {
      // storage unavailable (e.g. private mode) - the choice just won't be remembered
    }
  }

  // Everything is fetched once; the Me/Everyone switch only filters what's already loaded
  const visibleUsers = useMemo(
    () => (view === 'me' ? users.filter((user) => user.id === currentUserId) : users),
    [view, users, currentUserId]
  )
  const visibleAbsences = useMemo(
    () =>
      view === 'me' ? absences.filter((absence) => absence.group === currentUserId) : absences,
    [view, absences, currentUserId]
  )

  // Non-admins may only delete their own absences
  const canDeleteFor = (userId: unknown) => isAdmin || userId === currentUserId

  async function getUserVacationDays() {
    try {
      // var response = await absenceApi.getAbsencesForUser(userId);
      var response = await userApi.getUserVacations(Number(localStorage.getItem('USERID')))
      setNewVacationDays(response.newVacationDays)
      setOldVacationDays(response.oldVacationDays)
      //setAbsences(absences);
    } catch (e) {
      if (e instanceof Error) {
        snackbarContext?.setShowSnackbarData(true, e.message, 'danger')
      }
    }
  }

  async function getAllAbsences() {
    try {
      // var response = await absenceApi.getAbsencesForUser(userId);
      var response = await absenceApi.getAbsences()
      var absences = setEvents(response)
      setAbsences(absences)
    } catch (e) {
      if (e instanceof Error) {
        snackbarContext?.setShowSnackbarData(true, e.message, 'danger')
      }
    }
  }

  async function deleteAbsence(absenceId: number) {
    try {
      await absenceApi.deleteAbsence(absenceId)
      await getAllAbsences()
      await getUserVacationDays()
      snackbarContext?.setShowSnackbarData(true, 'Absence deleted', 'success')
    } catch (e) {
      if (e instanceof Error) {
        snackbarContext?.setShowSnackbarData(true, e.message, 'danger')
      }
    }
  }

  async function getAllUsers() {
    try {
      var temp = new Array<TimelineGroupBase>()
      var response = await userApi.getUsers()
      response.forEach((user) =>
        temp.push({ id: user.id, title: `${user.fullName}`, stackItems: true })
      )
      setUsers(temp)

      const usersArray = temp.map((user: TimelineGroupBase) => {
        return new ILabel(user.title as string, user.id)
      })
      setFormUsers(usersArray)

      setLoading(false)
    } catch (e) {
      if (e instanceof Error) {
        snackbarContext?.setShowSnackbarData(true, e.message, 'danger')
      }
    }
  }

  function setEvents(absences: Absence[]): TimelineItemBase<Moment>[] {
    var events: TimelineItemBase<Moment>[] = []

    absences.forEach((a) => {
      var event: TimelineItemBase<Moment> = {
        id: a.id,
        group: a.user.id,
        title: getAbsenceLabel(a.absenceReason),
        start_time: moment(a.absenceFrom),
        end_time: moment(a.absenceTo).add(1, 'day'),
        className:
          'timeline-item-' +
          `${a.absenceReason}` +
          (canDeleteFor(a.user.id) ? '' : ' timeline-item-readonly'),
        itemProps: {
          title: `${a.user.fullName} · ${getAbsenceLabel(a.absenceReason)}: ${moment(
            a.absenceFrom
          ).format('DD.MM.')} – ${moment(a.absenceTo).format('DD.MM.YYYY.')}`,
        },
      }
      events.push(event)
    })
    return events
  }

  function handleUserVacationDays() {
    setCreateVacationsModal(true)
  }
  return (
    <Container>
      <GenericContentPage
        header={{
          title: 'Absences',
          buttonFunc: setCreateModal,
          buttonTitle: 'Add Absence',
          icon: addIcon,
          actions: isAdmin && (
            <button className="addButton secondary" type="button" onClick={handleUserVacationDays}>
              <img className="addButtonIcon" src={editIcon} alt="" />
              User vacation days
            </button>
          ),
        }}
      >
        <>
          {loading ? (
            <AbsenceSkeleton />
          ) : (
            <div className={styles.page}>
              <Vacation
                newVacationDays={Number(newVacationDays)}
                oldVacationDays={Number(oldVacationDays)}
              />
              <CustomTimeLine
                absences={visibleAbsences}
                users={visibleUsers}
                view={view}
                onViewChange={changeView}
                canDelete={(absenceId) =>
                  canDeleteFor(absences.find((absence) => absence.id === absenceId)?.group)
                }
                isAdmin={isAdmin}
                setDeleteModal={setDeleteModal}
                setDeleteData={setData}
              />
            </div>
          )}
          <DeleteModal
            isOpen={deleteModal}
            setDeleteModal={setDeleteModal}
            setDeleteData={setData}
            data={data}
            delete={deleteAbsence}
          />
          <AbsenceCreateModal
            isOpen={createModal}
            setCreateModal={setCreateModal}
            absence={absence}
            getUserAbsences={getAllAbsences}
            getUserVacationDays={getUserVacationDays}
            setAbsenceRequest={setAbsence}
            users={formUsers}
            isAdmin={isAdmin}
          />
          <VacationCreateModal
            isOpen={createVacationsModal}
            setCreateVacationModal={setCreateVacationsModal}
            users={formUsers}
          />
        </>
      </GenericContentPage>
    </Container>
  )
}

export default AbsencesLayout
